import React, { useEffect, useMemo, useState } from 'react';
import {
  Animate,
  AnswerType,
  AnswerValueType,
  Flex,
  Spinner,
  SurveyQuestion,
} from 'component-library';
import { makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useSurveyQuestions } from 'lib/useSurveyQuestions';
import {
  AttestationEventTypeEnum,
  Page,
  ProgramNameEnum,
  ProgramStageEnum,
  ProgramSubStageEnum,
  QualificationModalStatusEnum,
  SurveyNameEnum,
} from 'lib/constants';
import { isProgramSectionViewed } from '../helpers';
import { CmsQuestionData } from 'lib/interfaces';
import {
  useCommonStores,
  useCompany,
  useDashboardStores,
  useFeatureFlags,
  useLegacyClients,
  useTaxCreditsStores,
} from 'stores/useStores';
import _ from 'lodash';
import { SurveyFlowContainer } from 'products/tax-credits/components';
import { QualificationStatusModal } from '../../../retirement-plan/components';
import { useEffectOnce } from 'component-library/_helpers/use-utils';
import { PrefillAlert } from '../../components/PrefillAlert';
import { AccumulatedModal } from '../../components';
import { SurveyAttestation } from 'pages/tax-processing/expense-classification/components/';
import { useExpenseClassificationAnswerPrefill } from 'lib/useExpenseClassificationAnswerPrefill';
import { buildCommonHandlers, handleContinueText } from '../commonHandlers';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  mainContent: {
    margin: '0 auto',
    position: 'relative',
  },
}));

interface UnifiedDisabledAccessCreditProps {
  onNext: () => void;
  onBack: () => void;
  taxYear: number;
}

export const UnifiedDisabledAccessCredit: React.FC<UnifiedDisabledAccessCreditProps> =
  observer(({ onNext, onBack, taxYear }) => {
    const classes = useStyles();
    const [showDqModal, setShowDqModal] = useState<boolean>(false);
    const { app, auth, chatbot, companyStore } = useCommonStores();
    const { surveyFlow, unifiedTaxCredits } = useTaxCreditsStores();
    const { company } = useCompany();
    const [hasConfirmedAnswers, setHasConfirmedAnswers] =
      useState<boolean>(false);
    const [validAnswer, setValidAnswer] = useState<boolean>(false);
    const featureFlags = useFeatureFlags();
    const { client } = useLegacyClients();
    const { modules } = useDashboardStores();
    const program =
      company.programs.find(
        (item) =>
          item.taxYear === taxYear && item.name === ProgramNameEnum.FED_RD_TAX,
      ) || null;
    const surveyProgram = company.programs.find(
      (p) =>
        p.taxYear === taxYear && p.name === ProgramNameEnum.FED_DISABLED_ACCESS,
    );

    // DQ takes precedence over opt-out (OQ-9): a DQ'd section gets no toggle
    const isDisqualified =
      surveyProgram?.stage === ProgramStageEnum.DISQUALIFIED;
    const isOptOutLocked = !!company.misc?.submittedTaxYears?.includes(taxYear);
    const isServerOptedOut =
      surveyProgram?.subStage ===
        ProgramSubStageEnum.EXPENSE_CLASSIFICATION_SURVEY_OPTED_OUT ||
      // post-submit the stamp is a plain CANCELED (substage nulled)
      (isOptOutLocked && surveyProgram?.stage === ProgramStageEnum.CANCELED);
    // The toggle is local until Continue persists it: flipping it collapses
    // or expands the questions instantly with no network write. null = not
    // seeded yet (survey content still loading); seeded below once the
    // section's questions and answers are known.
    const [pendingOptOut, setPendingOptOut] = useState<boolean | null>(null);
    const isOptedOut = pendingOptOut ?? isServerOptedOut;
    const allSurveysViewed = useMemo(() => {
      return companyStore.company.programs
        .filter((program) => program.taxYear === taxYear)
        .every(isProgramSectionViewed);
    }, [companyStore.company.programs, taxYear]);

    const [loadingPrefill, setLoadingPrefill] = useState(true);

    const surveyName: SurveyNameEnum =
      SurveyNameEnum.UNIFIED_DISABLED_ACCESS_CREDIT_SURVEY;

    const {
      isSurveyComplete,
      isLoading,
      hasAnyAnswers,
      surveyAnswers,
      questionsToRender,
      addSurveyAnswers,
      saveAnswers,
      addSurveyAnswersWithoutRerender,
    } = useSurveyQuestions(surveyName, taxYear, undefined, undefined);

    // Default-off: a section that has not been answered at all seeds opted
    // out; any saved answer (including payroll prefill) seeds it on. Seeds
    // exactly once — never re-run as the customer answers, so the section
    // cannot collapse mid-edit. hasAnyAnswers === null means the question
    // tree isn't built yet (loading, CMS error, or empty survey): never
    // seed on unknown — the display falls back to the server state until
    // we can actually tell. Seed-once also means a server opt-out arriving
    // AFTER the seed (a concurrent edit from another session/device) won't
    // re-collapse the section; accepted so a background refresh can never
    // yank the questions away mid-edit.
    useEffect(() => {
      if (pendingOptOut !== null) {
        return;
      }
      // Invitees (shared-assessment access token) get no opt-out toggle, so
      // never seed for them: the section follows the server state instead
      // of collapsing behind a control they can't see or undo.
      if (companyStore.accessToken) {
        return;
      }
      if (isDisqualified) {
        setPendingOptOut(false);
        return;
      }
      // A locked toggle (year already submitted) can't be operated, so a
      // seeded default would pose a question the customer can't answer —
      // mirror the server state instead. Keeps an ops-reopened pristine
      // section expanded rather than dead-ending it collapsed.
      if (isOptOutLocked) {
        setPendingOptOut(isServerOptedOut);
        return;
      }
      // Payroll prefill (SetYeaPrefill + company refresh) lands after mount;
      // judging before it lands would seed a prefill-only section OFF.
      if (loadingPrefill) {
        return;
      }
      if (hasAnyAnswers === null) {
        return;
      }
      setPendingOptOut(isServerOptedOut || !hasAnyAnswers);
    }, [
      hasAnyAnswers,
      isServerOptedOut,
      isDisqualified,
      isOptOutLocked,
      pendingOptOut,
      loadingPrefill,
      companyStore.accessToken,
    ]);

    // Prefill answers from other EC/Qual questions
    useExpenseClassificationAnswerPrefill(
      surveyName,
      program,
      addSurveyAnswersWithoutRerender,
    );

    useEffectOnce(async () => {
      // create program if doesn't exist
      await surveyFlow.createTaxCreditProgram(
        taxYear,
        ProgramNameEnum.FED_DISABLED_ACCESS,
      );
      // update program stage to expense_classification onload
      await unifiedTaxCredits.updateProgramStageOnSurveyEntry(
        taxYear,
        ProgramNameEnum.FED_DISABLED_ACCESS,
      );
    });

    useEffect(() => {
      // scroll page down as user answers each question
      scrollIntoViewOnKeyDown();
      // checking survey answers to display qualification modal
      surveyFlow.checkQualificationStatus(surveyAnswers);

      const hasDisableAccessDQ = surveyFlow.allQualificationStatuses.find(
        (status) =>
          status.status === QualificationModalStatusEnum.DISABLED_ACCESS_DQ,
      );

      if (hasDisableAccessDQ) {
        setShowDqModal(true);
      } else {
        setShowDqModal(false);
      }
    }, [surveyFlow, surveyAnswers]);

    useEffectOnce(async () => {
      if (featureFlags.showShareAssessment) {
        unifiedTaxCredits.setShareAssessmentOnNext(onNext);
      }

      await client.SetYeaPrefill(taxYear);
      await app.common.companyStore.refreshCurrentCompany();
      setLoadingPrefill(false);
    });

    const { handleOnContinue, disqualifyProgram } = buildCommonHandlers(
      surveyFlow,
      unifiedTaxCredits,
      companyStore,
      auth,
      modules,
      app,
      client,
      taxYear,
      surveyProgram,
      QualificationModalStatusEnum.DISABLED_ACCESS_DQ,
      AttestationEventTypeEnum.YEAR_END_ASSESSMENT_DISABLED_ACCESS,
      hasConfirmedAnswers,
      featureFlags.saveYeaSurveyAttestation,
      onNext,
      saveAnswers,
    );

    const handleAnswerChange = (
      question: CmsQuestionData,
      answerValue: AnswerValueType,
      key: string,
    ) => {
      setValidAnswer(answerValue > 0);
      question.answerValue = answerValue;
      addSurveyAnswers(question.id, key, [
        { questionId: question.id, answerValue: answerValue },
      ]);
      // save answers per question answered
      if (
        question.answerType === 'yes_or_no' ||
        question.answerType === 'multiple_choice'
      ) {
        saveAnswers && saveAnswers();
      }
    };

    const handleOnDisqualified = async () => {
      surveyFlow.setModalContinueIsLoading(true);

      await disqualifyProgram();

      onNext();
    };

    const continueText = handleContinueText(
      companyStore.accessToken,
      allSurveysViewed,
      'Continue',
    );

    const scrollIntoViewOnKeyDown = () => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    };

    const renderQuestions = () =>
      Object.keys(questionsToRender).map((key) => {
        return questionsToRender[key].map((question) => {
          return (
            <Animate
              key={question.id}
              enter={['fade-in', 'from-bottom']}
              duration={0.5}
            >
              <SurveyQuestion
                dataTestId={question.id}
                answerType={question.answerType as AnswerType}
                text={question.text}
                subtitle={question.subtitle}
                answerOptions={question.answerIDs}
                onChange={(answer) => handleAnswerChange(question, answer, key)}
                onBlur={() => saveAnswers && saveAnswers()}
                placeholder={question.placeholder}
                answerValue={question.answerValue}
                tooltip={question.tooltip}
                questionTextSize={app.isMobile ? 15 : 18}
                questionSubtitleSize={app.isMobile ? 13 : 15}
                withCardMargin={false}
                helpLinkText={question.helperText}
                chatBotQuestion={chatbot.isAiQuestionEnabled(
                  question.aiQuestion,
                )}
                chatBotPrompt={() =>
                  chatbot.autoSendAiMessage(question.aiQuestion)
                }
                onHelpLinkClick='https://mainstreet1.my.site.com/help/s/article/Eligible-Costs-for-Disabled-Access-Tax-Credit'
              />
            </Animate>
          );
        });
      });

    return (
      <Flex
        direction='column'
        className={classes.root}
        data-testid={'unified-disabled-access-credit'}
      >
        <Flex className={classes.mainContent} direction='column'>
          <Animate enter={'fade-in'}>
            <SurveyFlowContainer
              title='Disabled Access Tax Credit'
              subtitle='The Disabled Access Tax Credit is a tax credit that was introduced as part of the Americans with Disabilities Act (ADA) of 1990. It is designed to encourage businesses to make their establishments more accessible to individuals with disabilities. The credit allows businesses to claim a portion of the expenses incurred for making their establishments more accessible to individuals with disabilities.'
              onOptOut={
                isDisqualified ? undefined : () => setPendingOptOut(true)
              }
              onReopen={
                isDisqualified ? undefined : () => setPendingOptOut(false)
              }
              isOptedOut={isOptedOut}
              isOptOutLocked={isOptOutLocked}
              isDisqualified={isDisqualified}
              onContinue={() => {
                // pending opt-out is persisted here, not on toggle click;
                // an unchanged, already-saved opt-out just advances
                if (isOptedOut) {
                  if (isServerOptedOut) {
                    // Invitees end their visit on the shared page, same as
                    // handleOnContinue: navigating would strand them on a
                    // route their share token can't drive.
                    if (companyStore.accessToken) {
                      unifiedTaxCredits.setShowInviteeSuccessModal(true);
                      return;
                    }
                    onNext();
                  } else {
                    surveyFlow.optOutSurveySection(
                      taxYear,
                      surveyProgram?.id ?? -1,
                      ProgramNameEnum.FED_DISABLED_ACCESS,
                      onNext,
                    );
                  }
                  return;
                }
                handleOnContinue();
              }}
              isDisabled={
                !isSurveyComplete ||
                (featureFlags.saveYeaSurveyAttestation &&
                  !hasConfirmedAnswers &&
                  !validAnswer)
              }
              isLoading={surveyFlow.surveyContinueLoading}
              continueText={continueText}
              currentPage={Page.disabledAccess}
              onBack={companyStore.accessToken ? undefined : onBack}
              onSkip={() =>
                surveyFlow.skipSurveyStep(
                  taxYear,
                  program?.id ?? -1,
                  ProgramNameEnum.FED_DISABLED_ACCESS,
                  onNext,
                )
              }
            >
              <Flex direction='column' gap={24}>
                <PrefillAlert />
                {isLoading || loadingPrefill ? (
                  <Spinner size='small' />
                ) : (
                  !_.isEmpty(questionsToRender) && renderQuestions()
                )}
                {showDqModal && (
                  <QualificationStatusModal
                    showModal={surveyFlow.showQualificationStatusModal}
                    modalOnClick={async () => {
                      await handleOnDisqualified();
                    }}
                    programName={ProgramNameEnum.FED_DISABLED_ACCESS}
                  />
                )}
                {featureFlags.saveYeaSurveyAttestation && isSurveyComplete && (
                  <SurveyAttestation
                    checked={hasConfirmedAnswers}
                    onAttestation={() =>
                      setHasConfirmedAnswers(!hasConfirmedAnswers)
                    }
                  />
                )}
              </Flex>
            </SurveyFlowContainer>
          </Animate>
        </Flex>
        <AccumulatedModal
          programName='Disabled Access'
          subtitle="Let's see how much you stand to claim across all your credits."
        />
      </Flex>
    );
  });
